import { forwardRef } from "react";
import type { ButtonHTMLAttributes, ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { cn } from "../../lib/utils";

export type ButtonVariant = "primary" | "secondary" | "soft" | "ghost" | "danger";
export type ButtonSize = "sm" | "md" | "lg";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Stretch to the container width. */
  block?: boolean;
  /** Shows a spinner and disables the button. */
  loading?: boolean;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
}

const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    "bg-primary text-primary-foreground shadow-glow-cyan hover:bg-primary/90",
  secondary:
    "bg-surface text-foreground border border-border hover:bg-surface-raised",
  soft: "bg-accent text-accent-foreground hover:bg-accent/80",
  ghost: "bg-transparent text-foreground hover:bg-surface",
  danger:
    "bg-destructive/10 text-destructive hover:bg-destructive/15",
};

const SIZES: Record<ButtonSize, string> = {
  sm: "min-h-[36px] px-4 text-sm gap-1.5",
  md: "min-h-[44px] px-5 text-[15px] gap-2",
  lg: "min-h-[56px] px-6 text-base gap-2",
};

/**
 * The app's single button primitive. Pill-shaped, ≥44px tall by default, with
 * a visible focus ring. Use `variant="primary"` for the one main action per
 * screen.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = "primary",
      size = "md",
      block = false,
      loading = false,
      leadingIcon,
      trailingIcon,
      className,
      children,
      disabled,
      type = "button",
      ...rest
    },
    ref
  ) => (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        "inline-flex items-center justify-center rounded-full font-semibold transition-all select-none",
        "active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        "disabled:opacity-50 disabled:pointer-events-none disabled:shadow-none",
        VARIANTS[variant],
        SIZES[size],
        block && "w-full",
        className
      )}
      {...rest}
    >
      {loading ? (
        <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
      ) : (
        leadingIcon
      )}
      {children}
      {!loading && trailingIcon}
    </button>
  )
);
Button.displayName = "Button";

/** Full-width primary call-to-action (e.g. "View Tasks"). */
export const PrimaryButton = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ size = "lg", block = true, ...rest }, ref) => (
    <Button ref={ref} variant="primary" size={size} block={block} {...rest} />
  )
);
PrimaryButton.displayName = "PrimaryButton";

export default Button;
